import { ComponentType, useRef } from 'react';

import Text from '@cartwhl/ui/base/Text';

import { type ArticleMeta } from '../types';
import APIAccessLink from './APIAccessLink';
import ArticleImage from './ArticleImage';
import ArticleVideo from './ArticleVideo';
import BlockQuote from './BlockQuote';
import './BlogArticleLayout.css';
import InlineMedia from './InlineMedia';
import MediaGrid from './MediaGrid';
import useArticleReadingTime from './useArticleReadingTime';
import YouTubeEmbed from './YouTubeEmbed';

const mdxComponents = {
  // Hide h1 — title is rendered from meta
  h1: () => null,
  h2: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <Text as="h2" variant="web/display/xl" className="mt-14 mb-12" {...props} />
  ),
  h3: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <Text
      as="h3"
      variant="web/body/medium/lg"
      className="mt-8 mb-3"
      {...props}
    />
  ),
  p: (props: React.HTMLAttributes<HTMLParagraphElement>) => (
    <Text
      as="p"
      variant="web/body/24"
      className="text-primary mb-8"
      {...props}
    />
  ),
  strong: (props: React.HTMLAttributes<HTMLElement>) => (
    <strong className="text-primary font-bold" {...props} />
  ),
  a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a
      className="hover:text-blue-60 text-blue-50 underline underline-offset-2 transition-colors"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  ul: (props: React.HTMLAttributes<HTMLUListElement>) => (
    <ul className="mb-8 ml-9 list-disc space-y-2" {...props} />
  ),
  ol: (props: React.HTMLAttributes<HTMLOListElement>) => (
    <ol className="mb-8 ml-11 list-decimal space-y-2" {...props} />
  ),
  li: (props: React.HTMLAttributes<HTMLLIElement>) => (
    <Text as="li" variant="web/body/24" className="text-primary" {...props} />
  ),
  blockquote: BlockQuote,
  hr: () => <hr className="border-primary my-12 border-t" />,
  APIAccessLink,
  ArticleImage,
  ArticleVideo,
  BlockQuote,
  MediaGrid,
  InlineMedia,
  YouTubeEmbed,
};

function formatDate(dateString: string): string {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  });
}

interface BlogArticleLayoutProps {
  meta: ArticleMeta;
  Content: ComponentType<{ components: Record<string, unknown> }>;
}

export default function BlogArticleLayout({
  meta,
  Content,
}: BlogArticleLayoutProps) {
  const articleBodyRef = useRef<HTMLDivElement | null>(null);
  const readingTimeMinutes = useArticleReadingTime({
    articleBodyRef,
    articleKey: meta.slug,
  });
  const byline = meta.author ? ` · By ${meta.author}` : '';

  return (
    <article className="article-layout container py-16 lg:py-24">
      {/* Header */}
      <header className="article-breakout mx-auto">
        <Text
          as="h1"
          variant="web/display/2xl"
          lg:variant="web/display/3xl"
          className="mb-4 max-w-[800px]"
        >
          {meta.title}
          {meta.subtitle ? ` — ${meta.subtitle}` : ''}
        </Text>
        <Text
          as="time"
          variant="web/body/24"
          className="text-tertiary mb-10 block lg:mb-14"
        >
          {formatDate(meta.date)}
          {byline} · {readingTimeMinutes} min read
        </Text>
      </header>

      {/* Article body — narrow column */}
      <div
        ref={articleBodyRef}
        className="article-body article-text-column mx-auto"
      >
        <Content components={mdxComponents} />
      </div>
    </article>
  );
}
